import { useMutation, useQueryClient } from "@tanstack/react-query";
import { recordInstanceKeys, recordKeys } from "./record.options";
import { recordService } from "./record.service";
import type {
	CreateRecordInfo,
	CreateRecordTypeInfo,
	EditRecordFileInfo,
	EditRecordInfo,
	EditRecordTypeInfo,
	RecordInstanceListParams,
	RecordTypesListParams,
	ServiceResult,
} from "./record.types";

/**
 * Create a record instance.
 * POST /records
 */
export const useCreateRecord = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateRecordInfo) => recordService.createRecord(data),
		onSuccess: () => {
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.counts(),
				}),
			]);
		},
		onError: (error) => {
			console.error("Failed to create record:", error);
		},
	});
};

/**
 * Update a record instance with cache invalidation.
 * PUT /records/{recordId}
 */
export const useUpdateRecord = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: EditRecordInfo }) =>
			recordService.updateRecord(id, data),
		onSuccess: (result: ServiceResult, { id }) => {
			// Do not await invalidation — details autosave shows Saved only after
			// mutateAsync settles. Blocking on detail/list refetches left fields
			// stuck on "Saving".
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: [...recordInstanceKeys.details(), id],
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.historyCount(id),
				}),
			]);
			return result;
		},
		onError: (error) => {
			console.error("Failed to update record:", error);
		},
	});
};

/**
 * Create a new record type
 * POST /records/types
 *
 * Pass `organizationId: EMPTY_ORG_ID` for platform (system) types, or the
 * actual org ID for organization (custom) types.
 */
export const useCreateRecordType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			data,
			organizationId,
		}: {
			data: CreateRecordTypeInfo;
			organizationId?: string;
		}) => recordService.createRecordType(data, { organizationId }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: recordKeys.types(),
			});
		},
		onError: (error) => {
			console.error("Failed to create record type:", error);
		},
	});
};

/**
 * Duplicate a record type. Cache refresh matches create; navigation stays with the caller.
 */
export const useDuplicateRecordType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			recordTypeId,
			organizationId,
		}: {
			recordTypeId: string;
			organizationId?: string;
		}) => recordService.duplicateRecordType(recordTypeId, { organizationId }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: recordKeys.types(),
			});
		},
		onError: (error) => {
			console.error("Failed to duplicate record type:", error);
		},
	});
};

/**
 * Delete a record instance
 * DELETE /records/{recordId}
 */
export const useDeleteRecord = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			organizationId,
		}: {
			id: string;
			organizationId?: string;
		}) => recordService.deleteRecord(id, { organizationId }),
		onSuccess: (_result, { id }) => {
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: [...recordInstanceKeys.details(), id],
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.counts(),
				}),
			]);
		},
		onError: (error) => {
			console.error("Failed to delete record:", error);
		},
	});
};

/**
 * Delete a record type
 * DELETE /records/types/{id}
 */
export const useDeleteRecordType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			organizationId,
		}: {
			id: string;
			organizationId?: string;
		}) => recordService.deleteRecordType(id, { organizationId }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: recordKeys.types() });
		},
		onError: (error) => {
			console.error("Failed to delete record type:", error);
		},
	});
};

/**
 * Update a record type
 * PUT /records/types/{id}
 */
export const useUpdateRecordType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			data,
			organizationId,
		}: {
			id: string;
			data: EditRecordTypeInfo;
			organizationId?: string;
		}) => recordService.updateRecordType(id, data, { organizationId }),
		onSuccess: (_result, variables) => {
			queryClient.invalidateQueries({ queryKey: recordKeys.types() });
			queryClient.invalidateQueries({
				queryKey: recordKeys.recordTypeDetail(variables.id),
			});
			queryClient.invalidateQueries({
				queryKey: recordKeys.recordTypeHistory(variables.id),
			});
		},
		onError: (error) => {
			console.error("Failed to update record type:", error);
		},
	});
};

export const useEnableRecordType = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			organizationId,
		}: {
			id: string;
			organizationId?: string;
		}) => recordService.enableRecordType(id, { organizationId }),
		onSuccess: (_result, variables) => {
			queryClient.invalidateQueries({ queryKey: recordKeys.types() });
			queryClient.invalidateQueries({
				queryKey: recordKeys.recordTypeDetail(variables.id),
			});
			queryClient.invalidateQueries({
				queryKey: recordKeys.recordTypeHistory(variables.id),
			});
		},
		onError: (error) => {
			console.error("Failed to enable record type:", error);
		},
	});
};

export const useDisableRecordType = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			organizationId,
		}: {
			id: string;
			organizationId?: string;
		}) => recordService.disableRecordType(id, { organizationId }),
		onSuccess: (_result, variables) => {
			queryClient.invalidateQueries({ queryKey: recordKeys.types() });
			queryClient.invalidateQueries({
				queryKey: recordKeys.recordTypeDetail(variables.id),
			});
			queryClient.invalidateQueries({
				queryKey: recordKeys.recordTypeHistory(variables.id),
			});
		},
		onError: (error) => {
			console.error("Failed to disable record type:", error);
		},
	});
};

export const useEnableRecordTypeSubOrganization = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			typeId,
			subOrgId,
			organizationId,
		}: {
			typeId: string;
			subOrgId: string;
			organizationId?: string;
		}) =>
			recordService.enableRecordTypeSubOrganization(typeId, subOrgId, {
				organizationId,
			}),
		onSuccess: (_result, { typeId, organizationId }) => {
			queryClient.invalidateQueries({ queryKey: recordKeys.types() });
			queryClient.invalidateQueries({
				queryKey: recordKeys.recordTypeDetail(
					typeId,
					undefined,
					organizationId,
				),
			});
			queryClient.invalidateQueries({
				queryKey: recordKeys.recordTypeHistory(typeId),
			});
		},
		onError: (error) => {
			console.error(
				"Failed to enable sub-organization for record type:",
				error,
			);
		},
	});
};

export const useDisableRecordTypeSubOrganization = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			typeId,
			subOrgId,
			organizationId,
		}: {
			typeId: string;
			subOrgId: string;
			organizationId?: string;
		}) =>
			recordService.disableRecordTypeSubOrganization(typeId, subOrgId, {
				organizationId,
			}),
		onSuccess: (_result, { typeId, organizationId }) => {
			queryClient.invalidateQueries({ queryKey: recordKeys.types() });
			queryClient.invalidateQueries({
				queryKey: recordKeys.recordTypeDetail(
					typeId,
					undefined,
					organizationId,
				),
			});
			queryClient.invalidateQueries({
				queryKey: recordKeys.recordTypeHistory(typeId),
			});
		},
		onError: (error) => {
			console.error(
				"Failed to disable sub-organization for record type:",
				error,
			);
		},
	});
};

/**
 * Export record types to CSV
 * GET /records/types/{start}/{count}/export
 */
export const useExportRecordTypes = () => {
	return useMutation({
		mutationFn: async ({
			startIndex,
			count,
			params,
		}: {
			startIndex: number;
			count: number;
			params: RecordTypesListParams & { columns?: string };
		}) => {
			const { columns, ...filters } = params;
			return recordService.exportRecordTypesCsv(
				startIndex,
				count,
				filters,
				columns,
			);
		},
		onError: (error) => {
			console.error("Record types export failed:", error);
		},
	});
};

/**
 * Export record instances to CSV.
 * GET /records/{start}/{count}/export
 */
export const useExportRecords = () => {
	return useMutation({
		mutationFn: async ({
			startIndex,
			count,
			params,
		}: {
			startIndex: number;
			count: number;
			params: RecordInstanceListParams & { columns?: string };
		}) => {
			return recordService.exportRecords(startIndex, count, params);
		},
		onError: (error) => {
			console.error("Records export failed:", error);
		},
	});
};

/**
 * Upload a record attachment.
 * POST /records/{recordId}/files
 */
export const useUploadRecordFile = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			recordId,
			file,
			organizationId,
			description,
			entityAttachmentRequirementTagId,
			onUploadProgress,
		}: {
			recordId: string;
			file: File | Blob;
			organizationId?: string;
			description?: string;
			entityAttachmentRequirementTagId?: string | null;
			onUploadProgress?: (percent: number) => void;
		}) =>
			recordService.uploadRecordFile(recordId, file, {
				organizationId,
				description,
				entityAttachmentRequirementTagId,
				onUploadProgress,
			}),
		onSuccess: (_result, { recordId }) => {
			queryClient.invalidateQueries({
				queryKey: recordInstanceKeys.files(recordId),
			});
		},
	});
};

/**
 * Delete a record attachment.
 * DELETE /records/{recordId}/files/{fileId}
 */
export const useDeleteRecordFile = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			recordId,
			fileId,
			organizationId,
		}: {
			recordId: string;
			fileId: string;
			organizationId?: string;
		}) => recordService.deleteRecordFile(recordId, fileId, { organizationId }),
		onSuccess: (_result, { recordId }) => {
			queryClient.invalidateQueries({
				queryKey: recordInstanceKeys.files(recordId),
			});
		},
	});
};

/**
 * Rename / update a record attachment.
 * PUT /records/{recordId}/files/{recordFileId}
 */
export const useEditRecordFile = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			recordId,
			fileId,
			name,
			description,
			organizationId,
		}: {
			recordId: string;
			fileId: string;
			name?: string | null;
			description?: string | null;
			organizationId?: string;
		}) =>
			recordService.editRecordFile(
				recordId,
				fileId,
				{ name, description } satisfies EditRecordFileInfo,
				{ organizationId },
			),
		onSuccess: (_result, { recordId }) => {
			queryClient.invalidateQueries({
				queryKey: recordInstanceKeys.files(recordId),
			});
		},
	});
};

/**
 * Create a record note.
 * POST /records/{recordId}/notes
 */
export const useCreateRecordNote = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			recordId,
			text,
			organizationId,
		}: {
			recordId: string;
			text: string;
			organizationId?: string;
		}) =>
			recordService.createRecordNote(recordId, { text }, { organizationId }),
		onSuccess: (_result, { recordId }) => {
			queryClient.invalidateQueries({
				queryKey: recordInstanceKeys.notes(recordId),
			});
		},
	});
};

/**
 * Update a record note.
 * PUT /records/{recordId}/notes/{noteId}
 */
export const useUpdateRecordNote = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			recordId,
			noteId,
			text,
			organizationId,
		}: {
			recordId: string;
			noteId: string;
			text: string;
			organizationId?: string;
		}) =>
			recordService.updateRecordNote(
				recordId,
				noteId,
				{ text },
				{ organizationId },
			),
		onSuccess: (_result, { recordId }) => {
			queryClient.invalidateQueries({
				queryKey: recordInstanceKeys.notes(recordId),
			});
		},
	});
};

/**
 * Delete a record note.
 * DELETE /records/{recordId}/notes/{noteId}
 */
export const useDeleteRecordNote = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			recordId,
			noteId,
			organizationId,
		}: {
			recordId: string;
			noteId: string;
			organizationId?: string;
		}) => recordService.deleteRecordNote(recordId, noteId, { organizationId }),
		onSuccess: (_result, { recordId }) => {
			queryClient.invalidateQueries({
				queryKey: recordInstanceKeys.notes(recordId),
			});
		},
	});
};
